/**
 * AiInstructionsBlock — bloque rico para editar `DetailedAiInstructions`
 * de una sección (Doc 24 §2.5).
 *
 * Reemplaza el `AiInstructionsPlaceholder` v1 que solo soportaba `purpose`.
 * Maneja los 5 sub-campos canónicos:
 *
 *   - `purpose` (textarea)
 *   - `must_include[]` (ListInput)
 *   - `must_avoid[]` (ListInput)
 *   - `examples.good` (textarea)
 *   - `examples.bad.content` + `examples.bad.explanation` (textareas)
 *
 * El componente es controlled — el padre maneja el state via dispatch.
 * Bilingüe via `activeLocale` (es/en); las copies del label vienen de
 * i18n `templates.json`.
 *
 * Backwards-compat: tolera `value` como `Record<string, string>` (shape
 * v1 legacy) — solo extrae `purpose` y deja los demás campos vacíos.
 */

import { Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { DetailedAiInstructions } from '../../../api/templates'

import { ListInput } from './ListInput'
import type { SupportedLocale } from '../../../i18n/config'

interface AiInstructionsBlockProps {
  locale: SupportedLocale
  /** Tolera `undefined` explícito (tsconfig `exactOptionalPropertyTypes`)
   * porque el padre lee desde `i18nSection?.ai_instructions` que puede
   * no existir aún en secciones recién creadas. */
  value?: DetailedAiInstructions | Record<string, string> | undefined
  onChange(next: DetailedAiInstructions): void
  readOnly?: boolean
}

/** Normaliza un value v1 legacy `Record<string, string>` o v2 a
 * `DetailedAiInstructions` con todos los campos definidos. Defensivo. */
function normalize(
  value: DetailedAiInstructions | Record<string, string> | undefined,
): Required<DetailedAiInstructions> {
  if (!value || typeof value !== 'object') {
    return {
      purpose: null,
      must_include: [],
      must_avoid: [],
      examples: null,
    }
  }
  // Detect v2 shape: tiene cualquiera de must_include / must_avoid / examples
  // estructurado, o purpose como string. v1 legacy es Record<string,string>
  // plano (con keys como `purpose`, `tone`, `do_not`, etc.).
  const v2 = value as DetailedAiInstructions
  if (
    Array.isArray(v2.must_include) ||
    Array.isArray(v2.must_avoid) ||
    (v2.examples && typeof v2.examples === 'object')
  ) {
    return {
      purpose: v2.purpose ?? null,
      must_include: v2.must_include ?? [],
      must_avoid: v2.must_avoid ?? [],
      examples: v2.examples ?? null,
    }
  }
  // v1 legacy shape — solo extraemos purpose.
  const legacy = value as Record<string, string>
  return {
    purpose: typeof legacy['purpose'] === 'string' ? legacy['purpose'] : null,
    must_include: [],
    must_avoid: [],
    examples: null,
  }
}

export function AiInstructionsBlock({
  locale,
  value,
  onChange,
  readOnly = false,
}: AiInstructionsBlockProps) {
  const { t } = useTranslation('templates')
  const current = normalize(value)

  function emit(patch: Partial<DetailedAiInstructions>) {
    const next: DetailedAiInstructions = {
      purpose: current.purpose,
      must_include: current.must_include,
      must_avoid: current.must_avoid,
      examples: current.examples,
      ...patch,
    }
    onChange(next)
  }

  function emitPurpose(newPurpose: string) {
    emit({ purpose: newPurpose || null })
  }

  function emitMustInclude(items: string[]) {
    emit({ must_include: items })
  }

  function emitMustAvoid(items: string[]) {
    emit({ must_avoid: items })
  }

  function emitGoodExample(text: string) {
    const examples = current.examples
    if (!text && !examples?.bad) {
      emit({ examples: null })
      return
    }
    emit({
      examples: {
        good: text || null,
        bad: examples?.bad ?? null,
      },
    })
  }

  function emitBadExampleContent(text: string) {
    const examples = current.examples
    if (!text && !examples?.bad?.explanation && !examples?.good) {
      emit({ examples: examples?.good ? { good: examples.good, bad: null } : null })
      return
    }
    const explanation = examples?.bad?.explanation ?? ''
    if (!text && !explanation) {
      emit({
        examples: examples?.good ? { good: examples.good, bad: null } : null,
      })
      return
    }
    emit({
      examples: {
        good: examples?.good ?? null,
        bad: { content: text, explanation },
      },
    })
  }

  function emitBadExampleExplanation(text: string) {
    const examples = current.examples
    const content = examples?.bad?.content ?? ''
    emit({
      examples: {
        good: examples?.good ?? null,
        bad: content || text ? { content, explanation: text } : null,
      },
    })
  }

  return (
    <details
      className="rounded-md border border-line bg-surface-muted/40 p-3"
      data-testid="ai-instructions-block"
    >
      <summary className="cursor-pointer text-xs font-medium text-ink-muted flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5 text-brand" />
        {t('designer.editor.ai_instructions_v2.block_title', { locale })}
      </summary>

      <p className="mt-2 text-[11px] text-ink-subtle italic">
        {t('designer.editor.ai_instructions_v2.block_helper')}
      </p>

      <div className="mt-3 space-y-4">
        {/* Purpose */}
        <label className="block">
          <span className="text-xs text-ink-muted">
            {t('designer.editor.ai_instructions_v2.purpose_label')}
          </span>
          <textarea
            value={current.purpose ?? ''}
            onChange={(e) => emitPurpose(e.target.value)}
            disabled={readOnly}
            rows={3}
            maxLength={2000}
            placeholder={
              t(
                'designer.editor.ai_instructions_v2.purpose_placeholder',
              ) as string
            }
            className="mt-1 w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm font-body leading-relaxed focus:outline-none focus:border-brand resize-y disabled:opacity-60"
            data-testid="ai-instructions-purpose"
          />
        </label>

        {/* Must include */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-ink-muted">
              {t('designer.editor.ai_instructions_v2.must_include_label')}
            </span>
          </div>
          <p className="text-[11px] text-ink-subtle mb-2">
            {t('designer.editor.ai_instructions_v2.must_include_helper')}
          </p>
          <ListInput
            items={current.must_include}
            onChange={emitMustInclude}
            placeholder={
              t(
                'designer.editor.ai_instructions_v2.must_include_placeholder',
              ) as string
            }
            addLabel={
              t('designer.editor.ai_instructions_v2.must_include_add') as string
            }
            maxItems={20}
            maxItemLength={400}
            readOnly={readOnly}
            testId="ai-instructions-must-include"
          />
        </div>

        {/* Must avoid */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-ink-muted">
              {t('designer.editor.ai_instructions_v2.must_avoid_label')}
            </span>
          </div>
          <p className="text-[11px] text-ink-subtle mb-2">
            {t('designer.editor.ai_instructions_v2.must_avoid_helper')}
          </p>
          <ListInput
            items={current.must_avoid}
            onChange={emitMustAvoid}
            placeholder={
              t(
                'designer.editor.ai_instructions_v2.must_avoid_placeholder',
              ) as string
            }
            addLabel={
              t('designer.editor.ai_instructions_v2.must_avoid_add') as string
            }
            maxItems={20}
            maxItemLength={400}
            readOnly={readOnly}
            testId="ai-instructions-must-avoid"
          />
        </div>

        {/* Examples accordion */}
        <details className="rounded-md border border-line bg-canvas p-2.5">
          <summary className="cursor-pointer text-xs font-medium text-ink-muted">
            {t('designer.editor.ai_instructions_v2.examples_label')}
          </summary>
          <p className="mt-1 text-[11px] text-ink-subtle italic">
            {t('designer.editor.ai_instructions_v2.examples_helper')}
          </p>

          <label className="block mt-3">
            <span className="text-xs text-ink-muted">
              {t('designer.editor.ai_instructions_v2.examples_good_label')}
            </span>
            <textarea
              value={current.examples?.good ?? ''}
              onChange={(e) => emitGoodExample(e.target.value)}
              disabled={readOnly}
              rows={4}
              maxLength={4000}
              placeholder={
                t(
                  'designer.editor.ai_instructions_v2.examples_good_placeholder',
                ) as string
              }
              className="mt-1 w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm font-body leading-relaxed focus:outline-none focus:border-brand resize-y disabled:opacity-60"
              data-testid="ai-instructions-examples-good"
            />
          </label>

          <label className="block mt-3">
            <span className="text-xs text-ink-muted">
              {t('designer.editor.ai_instructions_v2.examples_bad_content_label')}
            </span>
            <textarea
              value={current.examples?.bad?.content ?? ''}
              onChange={(e) => emitBadExampleContent(e.target.value)}
              disabled={readOnly}
              rows={4}
              maxLength={4000}
              placeholder={
                t(
                  'designer.editor.ai_instructions_v2.examples_bad_content_placeholder',
                ) as string
              }
              className="mt-1 w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm font-body leading-relaxed focus:outline-none focus:border-brand resize-y disabled:opacity-60"
              data-testid="ai-instructions-examples-bad-content"
            />
          </label>

          <label className="block mt-3">
            <span className="text-xs text-ink-muted">
              {t(
                'designer.editor.ai_instructions_v2.examples_bad_explanation_label',
              )}
            </span>
            <textarea
              value={current.examples?.bad?.explanation ?? ''}
              onChange={(e) => emitBadExampleExplanation(e.target.value)}
              disabled={readOnly}
              rows={3}
              maxLength={2000}
              placeholder={
                t(
                  'designer.editor.ai_instructions_v2.examples_bad_explanation_placeholder',
                ) as string
              }
              className="mt-1 w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm font-body leading-relaxed focus:outline-none focus:border-brand resize-y disabled:opacity-60"
              data-testid="ai-instructions-examples-bad-explanation"
            />
          </label>
        </details>
      </div>
    </details>
  )
}
